import { useMutation, useQuery } from '@tanstack/react-query'

import {
  checkVerificationStatus,
  forgotPassword,
  getCurrentUser,
  getQQAuthorizeUrl,
  getRecommendedQQGroups,
  getVerificationServiceStatus,
  login,
  logout,
  registerAccount,
  registerGameAccount,
  resetPassword,
  verifyRegisterCode,
} from '@/api/authApi'
import type {
  ForgotPasswordInput,
  LoginInput,
  RegisterAccountInput,
  RegisterGameInput,
  RegisterVerifyInput,
  ResetPasswordInput,
} from '@/types/auth'

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
  verification: (qqNumber: string, type: string) =>
    [...authKeys.all, 'verification', qqNumber, type] as const,
  recommendedGroups: (limit: number) => [...authKeys.all, 'recommended-groups', limit] as const,
  verificationServiceStatus: () => [...authKeys.all, 'verification-service-status'] as const,
}

export function useLoginMutation() {
  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
  })
}

export function useRegisterAccountMutation() {
  return useMutation({
    mutationFn: (input: RegisterAccountInput) => registerAccount(input),
  })
}

export function useRegisterVerifyMutation() {
  return useMutation({
    mutationFn: (input: RegisterVerifyInput) => verifyRegisterCode(input),
  })
}

export function useRegisterGameMutation() {
  return useMutation({
    mutationFn: (input: RegisterGameInput) => registerGameAccount(input),
  })
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: (input: ForgotPasswordInput) => forgotPassword(input),
  })
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => resetPassword(input),
  })
}

export function useLogoutMutation() {
  return useMutation({
    mutationFn: () => logout(),
  })
}

export function useVerificationStatus(
  qqNumber: string,
  type: 'register' | 'reset_password',
  enabled: boolean
) {
  return useQuery({
    queryKey: authKeys.verification(qqNumber, type),
    queryFn: () => checkVerificationStatus(qqNumber, type),
    enabled: enabled && qqNumber.trim().length > 0,
    refetchInterval: query => (query.state.data?.verified ? false : 3000),
  })
}

export function useCurrentUserQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: getCurrentUser,
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useRecommendedQQGroups(limit: number = 3) {
  return useQuery({
    queryKey: authKeys.recommendedGroups(limit),
    queryFn: () => getRecommendedQQGroups(limit),
    staleTime: 60 * 1000,
  })
}

export function useVerificationServiceStatus() {
  return useQuery({
    queryKey: authKeys.verificationServiceStatus(),
    queryFn: getVerificationServiceStatus,
    staleTime: 15 * 1000,
    refetchInterval: 15 * 1000,
  })
}

export function useQQAuthorizeUrl() {
  return useQuery({
    queryKey: [...authKeys.all, 'qq-authorize-url'] as const,
    queryFn: getQQAuthorizeUrl,
    staleTime: 5 * 60 * 1000,
  })
}
